import React, { useState, useEffect, useCallback, useRef } from 'react';
import WhatsAppSendButton from '../../components/WhatsAppSendButton';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import { buildQcReportHtml } from './qcPrintReport';
import QcInspectionForm from './QcInspectionForm';
import { pointsFor, savedPoints, guideFor } from '../../utils/qcGuide';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Plus, Trash2, Send, Printer, Image as ImageIcon, X, Lock, Unlock,
  UserPlus, CheckCircle2, AlertTriangle, Loader2, GripVertical, Edit3
} from 'lucide-react';

const STATUS_META = {
  draft:     { label: 'Draft', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  assigned:  { label: 'Assigned', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  submitted: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  approved:  { label: 'Approved — Locked', cls: 'bg-green-100 text-green-700 border-green-300' },
  rejected:  { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-300' },
};

export default function QualityReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker/quality' : '/admin/quality';
  const { canWrite, canEdit } = usePermission('quality_report');
  const { user } = useAuth();
  const { settings } = useSettings() || {};
  const isAdmin = user?.role === 'admin';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploadingSec, setUploadingSec] = useState(null);
  const [newSection, setNewSection] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const fileRef = useRef(null);
  const uploadCtx = useRef(null);

  const status = report?.status;
  const isLocked = ['submitted', 'approved'].includes(status);
  const isAssignedWorker = report?.assigned_to === user?.id;
  const canUpload = !isLocked && (isAdmin || isAssignedWorker) && canWrite;
  const canAddSection = !isLocked && (isAdmin || (isAssignedWorker && report?.can_add_sections)) && canWrite;
  // Report details (client, SKU, QC person, ...) — same rule as the backend PUT
  const canEditDetails = ['draft', 'assigned', 'rejected'].includes(status) && (isAdmin || canEdit);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quality/reports/${id}`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load report');
      navigate(basePath);
    } finally { setLoading(false); }
  }, [id, navigate, basePath]);

  useEffect(() => { load(); }, [load]);

  const addSection = async () => {
    const name = newSection.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(`/quality/reports/${id}/sections`, { section_name: name });
      setNewSection(''); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setBusy(false); }
  };

  const delSection = async (sec) => {
    if (!window.confirm(`Delete section "${sec.section_name}" and its images?`)) return;
    try { await api.delete(`/quality/sections/${sec.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const pickImages = (sec) => { uploadCtx.current = sec; fileRef.current?.click(); };
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    const sec = uploadCtx.current;
    if (!files.length || !sec) return;
    setUploadingSec(sec.id);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('images', f));
      await api.post(`/quality/sections/${sec.id}/images`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded'); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Upload failed'); }
    finally { setUploadingSec(null); }
  };

  const editCaption = async (img) => {
    const caption = window.prompt('Caption for this image:', img.caption || '');
    if (caption === null) return;
    try { await api.put(`/quality/images/${img.id}`, { caption }); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };
  const delImage = async (img) => {
    if (!window.confirm('Delete this image?')) return;
    try { await api.delete(`/quality/images/${img.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const submit = async () => {
    if (!window.confirm('Submit this quality report for approval? It will be locked until the admin decides.')) return;
    setBusy(true);
    try { await api.post(`/quality/reports/${id}/submit`); toast.success('Submitted for approval'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Submit failed'); }
    finally { setBusy(false); }
  };

  // ── One print job: cover + inspection form + QC image pages (paged.js) ────
  const handlePrint = async () => {
    if (!report) return;
    if (formDirty && !window.confirm('The inspection form has unsaved changes. Print the last saved version?')) return;
    // Open first (synchronously) so the browser doesn't block the pop-up, then fill it
    const win = window.open('', '_blank');
    if (!win) { toast.error('Allow pop-ups for this site to print the report'); return; }
    win.document.write('<p style="font:600 13px Arial;color:#6b7280;padding:24px">Preparing report…</p>');
    try {
      const res = await api.get(`/quality/reports/${id}`);   // always print the latest saved data
      win.document.open();
      win.document.write(buildQcReportHtml(res.data, settings || {}));
      win.document.close();
    } catch (err) {
      win.close();
      toast.error(err.response?.data?.message || 'Could not load the report for printing');
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (!report) return null;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(basePath)} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-800">{report.report_no}</h1>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${STATUS_META[status].cls}`}>
              {STATUS_META[status].label}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handlePrint} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700">
            <Printer className="w-4 h-4" /> Print PDF
          </button>
          <WhatsAppSendButton
            documentType="QUALITY_REPORT"
            referenceId={report.report_no || id}
            fileName={`Quality Report ${report.report_no || id}.pdf`}
            getHtml={async () => {
              if (formDirty && !window.confirm('The inspection form has unsaved changes. Send the last saved version?')) throw new Error('Cancelled');
              const res = await api.get(`/quality/reports/${id}`);
              return buildQcReportHtml(res.data, settings || {});
            }}
          />
          {canEditDetails && (
            <button onClick={() => setShowEdit(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700">
              <Edit3 className="w-4 h-4" /> Edit details
            </button>
          )}
          {isAdmin && !['submitted', 'approved'].includes(status) && canEdit && (
            <button onClick={() => setShowAssign(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 text-sm font-semibold">
              <UserPlus className="w-4 h-4" /> Assign
            </button>
          )}
          {!isLocked && (isAdmin || isAssignedWorker) && canWrite && ['assigned', 'rejected'].includes(status) && (
            <button onClick={submit} disabled={busy} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for Approval
            </button>
          )}
        </div>
      </div>

      {/* Banners */}
      {status === 'rejected' && report.rejected_reason && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <div><strong>Rejected{report.rejected_by_name ? ` by ${report.rejected_by_name}` : ''}:</strong> {report.rejected_reason}
            <div className="text-xs mt-1">Update the report and submit again.</div></div>
        </div>
      )}
      {status === 'submitted' && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
          <Lock className="w-4 h-4" /> Pending approval — locked. The admin can approve or reject from the Approvals page.
        </div>
      )}
      {status === 'approved' && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4" /> Approved{report.approved_by_name ? ` by ${report.approved_by_name}` : ''} and locked (final).
        </div>
      )}
      {status === 'draft' && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
          <UserPlus className="w-4 h-4" /> Draft — assign to a worker (or keep for yourself) to begin uploading section images.
        </div>
      )}

      {/* Meta card */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
          <Meta k="Project" v={report.project_code || report.project_name_live} />
          <Meta k="Product" v={report.item_name} />
          <Meta k="Client" v={report.client_name} />
          <Meta k="SKU" v={report.sku_number} />
          <Meta k="QC Person" v={report.qc_person_name} />
          <Meta k="Level" v={(report.inspection_level || '').replace('_', ' ')} />
          <Meta k="Inspection Date" v={report.inspection_date ? new Date(report.inspection_date).toLocaleDateString('en-IN') : null} />
          <Meta k="Assigned To" v={report.assigned_to_name} />
        </div>
        {report.can_add_sections && (
          <p className="text-xs text-blue-600 mt-2 flex items-center gap-1"><Unlock className="w-3 h-3" /> Assigned worker may add sections</p>
        )}
      </div>

      {/* Inspection form (printed page 2) */}
      <QcInspectionForm report={report} editable={canUpload} onSaved={load} onDirtyChange={setFormDirty} />

      {/* Sections */}
      <div className="space-y-3">
        {(report.sections || []).map((sec, idx) => (
          <div key={sec.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-400">{idx + 1}</span>
                <span className="font-semibold text-gray-800">{sec.section_name}</span>
                <span className="text-xs text-gray-400">({(sec.images || []).length})</span>
              </div>
              <div className="flex items-center gap-1.5">
                {canUpload && (
                  <button onClick={() => pickImages(sec)} disabled={uploadingSec === sec.id}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-white disabled:opacity-50">
                    {uploadingSec === sec.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />} Upload
                  </button>
                )}
                {canAddSection && (
                  <button onClick={() => delSection(sec)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            <div className="p-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_340px]">
              <div className="min-w-0">
              {(sec.images || []).length === 0 ? (
                <p className="text-xs text-gray-300 py-4 text-center">No images in this section</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {sec.images.map(img => (
                    <div key={img.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                      <a href={buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
                        <img src={buildImageUrl(img.image_path)} alt={img.caption || ''} className="w-full h-28 object-cover" />
                      </a>
                      {img.corner_note && <span className="absolute top-1 right-1 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{img.corner_note}</span>}
                      {img.caption && <p className="text-[11px] px-2 py-1 text-gray-600 truncate">{img.caption}</p>}
                      {canUpload && (
                        <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => editCaption(img)} className="bg-white/90 rounded p-1 text-gray-600"><Edit3 className="w-3 h-3" /></button>
                          <button onClick={() => delImage(img)} className="bg-red-500 rounded p-1 text-white"><X className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </div>
              <CheckPoints sec={sec} editable={canUpload} onSaved={load} />
            </div>
          </div>
        ))}

        {canAddSection && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-3 flex gap-2">
            <input value={newSection} onChange={e => setNewSection(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSection()}
              placeholder="New section name (e.g. Load Test, Fabric Finish Sample)"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <button onClick={addSection} disabled={busy || !newSection.trim()}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">
              <Plus className="w-4 h-4" /> Add Section
            </button>
          </div>
        )}
        {!report.sections?.length && !canAddSection && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">No sections yet.</div>
        )}
      </div>

      {showEdit && <EditDetailsModal report={report} onClose={() => setShowEdit(false)} onDone={() => { setShowEdit(false); load(); }} />}
      {showAssign && <AssignModal report={report} onClose={() => setShowAssign(false)} onDone={() => { setShowAssign(false); load(); }} />}
    </div>
  );
}

function Meta({ k, v }) {
  return <div><div className="text-xs font-semibold text-gray-400 uppercase">{k}</div><div className="text-gray-800 capitalize">{v || '—'}</div></div>;
}

// Local yyyy-mm-dd for a DATE value from the API (avoids the UTC off-by-one day).
function toDateInput(v) {
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Edit the report's details (not the project — a report belongs to one project).
function EditDetailsModal({ report, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [qcPersons, setQcPersons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    project_item_id: report.project_item_id ? String(report.project_item_id) : '',
    client_name: report.client_name || '',
    sku_number: report.sku_number || '',
    qc_person_id: report.qc_person_id ? String(report.qc_person_id) : '',
    inspection_level: report.inspection_level || 'final',
    inspection_date: toDateInput(report.inspection_date),
    can_add_sections: Boolean(report.can_add_sections),
  });
  const set = (p) => setF((s) => ({ ...s, ...p }));

  useEffect(() => {
    if (report.project_id) {
      api.get(`/projects/${report.project_id}`)
        .then((r) => setItems(r.data.items || r.data.project_items || []))
        .catch(() => setItems([]));
    }
    api.get('/quality/qc-persons').then((r) => setQcPersons(r.data || [])).catch(() => {});
  }, [report.project_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/quality/reports/${report.id}`, {
        project_item_id: f.project_item_id ? Number(f.project_item_id) : undefined,
        client_name: f.client_name.trim(),
        sku_number: f.sku_number.trim(),
        qc_person_id: f.qc_person_id ? Number(f.qc_person_id) : undefined,
        inspection_level: f.inspection_level,
        inspection_date: f.inspection_date || undefined,
        can_add_sections: f.can_add_sections,
      });
      toast.success('Report details updated');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const label = 'block text-xs font-semibold text-gray-500 mb-1';
  const input = 'w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">Edit Report Details</h3>
            <p className="text-xs text-gray-500">{report.report_no} · Project {report.project_code || report.project_name_live || '—'}</p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={label}>Product / Item</label>
            <select className={input} value={f.project_item_id} onChange={(e) => set({ project_item_id: e.target.value })}>
              <option value="">— Not set —</option>
              {items.map((it) => <option key={it.id} value={it.id}>{it.item_name}{it.proto_code ? ` (${it.proto_code})` : ''}</option>)}
            </select>
          </div>
          <div><label className={label}>Client Name</label><input className={input} value={f.client_name} onChange={(e) => set({ client_name: e.target.value })} /></div>
          <div><label className={label}>SKU Number</label><input className={input} value={f.sku_number} onChange={(e) => set({ sku_number: e.target.value })} /></div>
          <div>
            <label className={label}>QC Person</label>
            <select className={input} value={f.qc_person_id} onChange={(e) => set({ qc_person_id: e.target.value })}>
              <option value="">— Select —</option>
              {qcPersons.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Inspection Level</label>
            <select className={input} value={f.inspection_level} onChange={(e) => set({ inspection_level: e.target.value })}>
              <option value="final">Final</option>
              <option value="white_body">White Body</option>
            </select>
          </div>
          <div><label className={label}>Inspection Date</label><input type="date" className={input} value={f.inspection_date} onChange={(e) => set({ inspection_date: e.target.value })} /></div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.can_add_sections} onChange={(e) => set({ can_add_sections: e.target.checked })} />
          Allow the assigned worker to add new sections
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ report, onClose, onDone }) {
  const [workers, setWorkers] = useState([]);
  const [assignedTo, setAssignedTo] = useState(report.assigned_to || '');
  const [canAdd, setCanAdd] = useState(!!report.can_add_sections);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/workers').then(r => setWorkers((r.data || []).filter(w => w.is_active !== 0))).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/quality/reports/${report.id}/assign`, { assigned_to: assignedTo || null, can_add_sections: canAdd });
      toast.success('Saved'); onDone();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Assign Report</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Assign to Worker</label>
          <select className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
            <option value="">— Unassigned —</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={canAdd} onChange={e => setCanAdd(e.target.checked)} />
          Allow this worker to add new sections
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-200">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Check points of one section — typed rows (Add Row / remove), printed in the section's box
function CheckPoints({ sec, editable, onSaved }) {
  const initial = () => pointsFor(sec).map(t => ({ k: Math.random().toString(36).slice(2), t }));
  const [rows, setRows] = useState(initial);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const isStandard = savedPoints(sec) === null && !!guideFor(sec.section_name);
  useEffect(() => { setRows(initial()); setDirty(false); }, [sec.id, sec.check_points]);
  const change = next => { setRows(next); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/quality/sections/${sec.id}`, { check_points: rows.map(r => r.t.trim()).filter(Boolean) });
      toast.success('Check points saved');
      setDirty(false);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-amber-200 rounded-lg bg-amber-50/40 self-start">
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-100">
        <span className="text-xs font-bold uppercase tracking-wide text-amber-900">Check Points</span>
        {editable && <button onClick={() => change([...rows, { k: Math.random().toString(36).slice(2), t: '' }])}
          className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-amber-300 bg-white text-[11px] font-semibold text-amber-800"><Plus className="w-3 h-3" /> Add Row</button>}
      </div>
      <div className="p-2 space-y-1.5">
        {rows.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">{editable ? 'No check points — use Add Row. Leave empty to print none.' : 'No check points'}</p>}
        {rows.map((r, i) => (
          <div key={r.k} className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            {editable ? <>
              <input value={r.t} maxLength={300} onChange={e => change(rows.map(x => (x.k === r.k ? { ...x, t: e.target.value } : x)))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); change([...rows.slice(0, i + 1), { k: Math.random().toString(36).slice(2), t: '' }, ...rows.slice(i + 1)]); } }}
                placeholder="e.g. Verify all dimensions" className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded-md text-xs bg-white" />
              <button onClick={() => change(rows.filter(x => x.k !== r.k))} title="Remove" className="p-1 rounded hover:bg-red-50 text-red-500"><X className="w-3 h-3" /></button>
            </> : <span className="text-xs text-gray-700">{r.t}</span>}
          </div>
        ))}
        {editable && (dirty || isStandard) && (
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-[10px] text-gray-400">{dirty ? 'Not saved yet' : 'Standard points — edit and save to change'}</span>
            {dirty && <button onClick={save} disabled={saving} className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50">{saving && <Loader2 className="w-3 h-3 animate-spin" />} Save</button>}
          </div>
        )}
      </div>
    </div>
  );
}
